/**
 * themeRuntime — applies a database-defined theme to the document.
 *
 * A theme is a flat { '--css-var': 'value' } token map. Applying one writes a single
 * <style id="dawn-theme-active"> element scoped to html.dawn-theme-<key> and tags <html>
 * with that class. Standard is applied from its row like every other theme; a missing or
 * malformed theme removes both, which falls back to the :root defaults in App.css (also
 * what paints before the first theme list arrives).
 *
 * Token values reach this module already validated by the API (strict whitelist), but
 * names and values are re-checked here because they are written into a <style> element.
 */

const STYLE_ID = 'dawn-theme-active';
const CLASS_PREFIX = 'dawn-theme-';
const LS_KEY = 'dawn.uiTheme';
const LS_TOKENS = 'dawn.uiThemeTokens';
const KEY_RE = /^[a-z0-9-]{1,50}$/;
const NAME_RE = /^--[a-z0-9-]+$/;
const VALUE_RE = /^[#a-zA-Z0-9(),.%\s-]+$/;
// A theme with a focus ring wider than 0 opts in to the ring; themes at 0 keep today's focus behavior untouched.
const FOCUS_RING_CLASS = 'dawn-focus-ring';


const safeEntries = (tokens) => {
  if (!tokens || typeof tokens !== 'object') return [];
  return Object.entries(tokens).filter(([n, v]) =>
    NAME_RE.test(n) && typeof v === 'string' && VALUE_RE.test(v));
};

/** CSS text for one rule: selector { --a: b; ... } */
export const tokensToCss = (selector, tokens) => {
  const body = safeEntries(tokens).map(([n, v]) => `${n}:${v};`).join('');
  return `${selector}{${body}}`;
};

const clearTheme = () => {
  const html = document.documentElement;
  [...html.classList].filter(c => c.startsWith(CLASS_PREFIX)).forEach(c => html.classList.remove(c));
  html.classList.remove(FOCUS_RING_CLASS);
  document.getElementById(STYLE_ID)?.remove();
};

/** Apply a theme to <html>. A missing key or missing tokens resets to the :root defaults. */
export const applyTheme = (key, tokens) => {
  clearTheme();
  if (!key || !KEY_RE.test(key) || safeEntries(tokens).length === 0) return;
  document.documentElement.classList.add(`${CLASS_PREFIX}${key}`);
  if (parseFloat(tokens['--focus-ring-width']) > 0) document.documentElement.classList.add(FOCUS_RING_CLASS);
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = tokensToCss(`html.${CLASS_PREFIX}${key}`, tokens);
  document.head.appendChild(el);
};

/** Remember the applied theme so the next page load paints it before any API call. */
export const cacheTheme = (key, tokens) => {
  try {
    localStorage.setItem(LS_KEY, key || 'standard');
    if (!key || !tokens) localStorage.removeItem(LS_TOKENS);
    else localStorage.setItem(LS_TOKENS, JSON.stringify(tokens));
  } catch { /* storage unavailable — cache is best-effort */ }
};

/** { key, tokens } from the boot cache; tokens is null when nothing usable is cached. */
export const readCachedTheme = () => {
  try {
    const key = localStorage.getItem(LS_KEY) || 'standard';
    const raw = localStorage.getItem(LS_TOKENS);
    return { key, tokens: raw ? JSON.parse(raw) : null };
  } catch { return { key: 'standard', tokens: null }; }
};

/** React style object of CSS custom properties — scopes a preview container to a theme. */
export const previewStyle = (tokens) => {
  const style = {};
  safeEntries(tokens).forEach(([n, v]) => { style[n] = v; });
  return style;
};
